import { addMinutes, startOfStockholmDay, stockholm, stockholmParts, TIME_ZONE } from '@cb/shared';
import { locale } from './i18n/index.ts';

export { addMinutes, startOfStockholmDay, stockholm, stockholmParts };

const pad = (n: number) => String(n).padStart(2, '0');

/** "2026-10-23" for <input type="date">, in Swedish time. */
export function toDateInput(d: Date): string {
  const p = stockholmParts(d);
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

/** "09:30" for <input type="time">, in Swedish time. */
export function toTimeInput(d: Date): string {
  const p = stockholmParts(d);
  return `${pad(p.hour)}:${pad(p.minute)}`;
}

export function fromInputs(date: string, time: string): Date | null {
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const tm = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dm || !tm) return null;
  return stockholm(+dm[1]!, +dm[2]!, +dm[3]!, +tm[1]!, +tm[2]!);
}

export const fmtTime = (d: Date | string) => toTimeInput(new Date(d));

export function fmtDate(
  d: Date | string,
  opts: Intl.DateTimeFormatOptions = { weekday: 'short', day: 'numeric', month: 'short' },
) {
  return new Intl.DateTimeFormat(locale(), { timeZone: TIME_ZONE, ...opts }).format(new Date(d));
}

export function fmtDateTime(d: Date | string) {
  return `${fmtDate(d, { day: 'numeric', month: 'short', year: 'numeric' })} ${fmtTime(d)}`;
}

/** "tis 3 nov 10:00–11:00", or both dates for a multi-day booking. */
export function fmtRange(start: Date | string, end: Date | string) {
  const s = new Date(start);
  const e = new Date(end);
  if (toDateInput(s) === toDateInput(addMinutes(e, -1)))
    return `${fmtDate(s)} ${fmtTime(s)}–${fmtTime(e)}`;
  return `${fmtDate(s)} ${fmtTime(s)} – ${fmtDate(e)} ${fmtTime(e)}`;
}

/** Monday 00:00 (Swedish time) of the week containing d. */
export function startOfWeek(d: Date): Date {
  return startOfStockholmDay(d, -stockholmParts(d).weekday);
}

export function startOfMonth(d: Date): Date {
  const p = stockholmParts(d);
  return stockholm(p.year, p.month, 1);
}

export function addMonths(d: Date, n: number): Date {
  const p = stockholmParts(d);
  return stockholm(p.year, p.month + n, 1);
}

/** Minutes in a Swedish calendar day: 1440, or 1380/1500 on clock-change days. */
export const dayMinutes = (day: Date) =>
  (startOfStockholmDay(day, 1).getTime() - day.getTime()) / 60_000;

/** Rounds to the nearest quarter hour. */
export const roundQuarter = (d: Date) => new Date(Math.round(d.getTime() / 900_000) * 900_000);

export function isToday(day: Date) {
  return toDateInput(day) === toDateInput(new Date());
}
